import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  DiffSourceToggleWrapper,
  InsertTable,
  InsertThematicBreak,
  ListsToggle,
  MDXEditor,
  Separator,
  UndoRedo,
  diffSourcePlugin,
  headingsPlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  markdownShortcutPlugin,
  quotePlugin,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  type MDXEditorMethods,
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { useThemeStore } from "@/store/themeStore";
import { cn } from "@/lib/utils";

/**
 * Props for the rich text editor. Intentionally identical to the props of
 * {@link MarkdownEditor} so the two editors are interchangeable.
 */
export interface RichTextEditorProps {
  /** The markdown content to edit. */
  value: string;
  /** Called with the updated markdown whenever the user edits the content. */
  onChange: (value: string) => void;
  /** Placeholder shown while the editor is empty. */
  placeholder?: string;
  /** Additional classes applied to the editor root. */
  className?: string;
}

/**
 * WYSIWYG editor that renders formatted content as the user types while reading
 * and writing plain markdown, so every existing markdown consumer (public quote
 * templates, cover letter blocks, previews) keeps working unchanged.
 *
 * Falls back to the plain-text {@link MarkdownEditor} when the content cannot be
 * parsed — legacy content containing characters that read as markup (`<`, `{`)
 * stays editable rather than failing to render.
 *
 * Loaded on demand by {@link RichTextEditor}; import that instead of this.
 *
 * @param props - The editor props
 * @returns The rich text editor component
 */
export function RichTextEditorContent({
  value,
  onChange,
  placeholder = "Write...",
  className,
}: RichTextEditorProps) {
  const editorRef = useRef<MDXEditorMethods>(null);
  const emittedValueRef = useRef(value);
  const [parseFailed, setParseFailed] = useState(false);
  const isDarkTheme = useThemeStore((state) => state.resolvedTheme === "dark");

  // The `markdown` prop is only read on mount, so external updates to `value`
  // have to be pushed in through the ref.
  useEffect(() => {
    if (value === emittedValueRef.current) {
      return;
    }
    emittedValueRef.current = value;
    editorRef.current?.setMarkdown(value);
  }, [value]);

  const handleChange = useCallback(
    (markdown: string, initialMarkdownNormalize: boolean) => {
      // Mount-time re-serialization would dirty content the user never touched.
      if (initialMarkdownNormalize) {
        return;
      }
      emittedValueRef.current = markdown;
      onChange(markdown);
    },
    [onChange],
  );

  const handleError = useCallback(() => {
    setParseFailed(true);
  }, []);

  // Lexical binds ⌘U to underline regardless of the toolbar, and underline has
  // no markdown representation — it serialises to a raw <u> tag, which the
  // renderers escape and show to the customer as literal "<u>" text. Swallowed
  // during capture so the editor never receives the keystroke.
  const handleKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!event.metaKey && !event.ctrlKey) {
        return;
      }
      if (event.key.toLowerCase() !== "u") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    },
    [],
  );

  if (parseFailed) {
    return (
      <MarkdownEditor
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={className}
      />
    );
  }

  return (
    <div
      onKeyDownCapture={handleKeyDownCapture}
      className={cn(
        "flex min-h-0 flex-col overflow-hidden rounded-md border border-input",
        className,
      )}
    >
      <MDXEditor
        ref={editorRef}
        markdown={value}
        onChange={handleChange}
        onError={handleError}
        placeholder={placeholder}
        suppressHtmlProcessing
        // Tailwind's preflight strips the heading and list styling MDXEditor
        // relies on, so the content area is styled with the same `prose` classes
        // the markdown previews use — headings look like headings, and what the
        // user edits matches what the preview renders.
        contentEditableClassName="rich-text-editor-content prose prose-sm dark:prose-invert max-w-none"
        // MDXEditor puts this class on both the editor root and its popup
        // container, so it may only carry styles that are safe on both.
        className={cn("rich-text-editor", isDarkTheme && "dark-theme")}
        plugins={[
          headingsPlugin(),
          listsPlugin(),
          quotePlugin(),
          thematicBreakPlugin(),
          linkPlugin(),
          linkDialogPlugin(),
          tablePlugin(),
          markdownShortcutPlugin(),
          diffSourcePlugin({ viewMode: "rich-text" }),
          toolbarPlugin({
            toolbarContents: () => (
              <DiffSourceToggleWrapper options={["rich-text", "source"]}>
                <UndoRedo />
                <Separator />
                {/* Underline is excluded — see handleKeyDownCapture. */}
                <BoldItalicUnderlineToggles options={["Bold", "Italic"]} />
                <Separator />
                <BlockTypeSelect />
                <Separator />
                <ListsToggle options={["bullet", "number"]} />
                <Separator />
                <CreateLink />
                <InsertThematicBreak />
                <InsertTable />
              </DiffSourceToggleWrapper>
            ),
          }),
        ]}
      />
    </div>
  );
}
